// **The shared match chart** (`match-hud` **D-5**).
//
// Extracted from `Summary.tsx`'s hand-rolled SVG, which already had this chart's whole shape:
// season bands, an unobserved-range shade, one polyline per colony over `toX`/`toY` scales, and a
// legend. The Match screen's statistics graph adds a stacked area (a `<polygon>` instead of a
// `<polyline>`), prompt markers (`<line>`) and series toggles — none of which is a reason for a
// second chart, and every one of which is a reason the two must not drift apart.
//
// **No charting library** (D-5, rejected: uPlot / Chart.js / Recharts). A library buys tooltips, a
// crosshair and axis autoscaling, and brings styling that would have to be fought back into the
// house treatment. Reconsider only for a hover crosshair with a per-series readout.
//
// **Data-driven series** (D-6): a caller hands over a list of `{kind, points, colour}` and this
// draws it, so a later statistic is a row in the caller's table rather than a new component here.
// The component owns geometry and nothing else — it computes no domain-specific value, resolves no
// colour, and reads no store.

import type { CSSProperties } from 'react';

/** The season split (DESIGN §14: 40 / 35 / 25), as chart bands. */
export const CHART_SEASON_BANDS = [
  { key: 'summer', label: 'Summer', from: 0, to: 0.4 },
  { key: 'autumn', label: 'Autumn', from: 0.4, to: 0.75 },
  { key: 'winter', label: 'Winter', from: 0.75, to: 1.0 },
] as const;

export interface ChartPoint {
  tick: number;
  value: number;
}

export interface ChartSeries {
  id: string;
  label: string;
  /** Any CSS colour, including a `var(--colony-N)` token — resolved by the caller's palette, never
   * here (D-3: no hardcoded red and blue anywhere). */
  colour: string;
  kind: 'line' | 'area';
  /** Ordered by ascending tick. */
  points: readonly ChartPoint[];
  /** An `area`'s lower edge, one value per point in the same order. Absent means the axis floor,
   * which is what makes the first band of a stack a plain filled region. */
  baseline?: readonly number[];
  strokeWidth?: number;
  opacity?: number;
  dashed?: boolean;
  /** Overrides the default `chart-series-<id>` test id (Summary keeps its own established one). */
  testId?: string;
  /**
   * Text drawn at the series' last point, in the right gutter, beside a small swatch in the
   * series colour.
   *
   * A direct label rather than a legend: at projector distance a legend is a lookup, and the
   * type palette carries two hues under 3:1 against the card surface, so identity may not rest on
   * colour alone. The **text is ink** and only the swatch wears the series colour.
   */
  endLabel?: string;
}

/** A vertical rule at a tick — the Match graph's prompt markers. */
export interface ChartMarker {
  id: string;
  tick: number;
  colour: string;
  title?: string;
  /** Drawn inside the dot under `markerStyle: 'axisDot'` — a prompt's ordinal, typically. */
  label?: string;
}

/**
 * **Axis furniture** (`spectator-hud-polish` W8): gridlines, tick labels and the gutters they
 * need. Absent — every caller that predates it — the plot fills the viewBox exactly as before and
 * not one coordinate moves, which is what keeps Summary's golden output stable.
 *
 * A chart with no scale is a shape, not a reading: the Match statistics graph shipped with no y
 * ticks, no gridlines and no time axis, so a viewer could see that a line rose without being able
 * to say from what to what, or when.
 */
export interface ChartAxes {
  /** Horizontal gridlines and their y labels, `ySteps + 1` of them counting the baseline. */
  ySteps?: number;
  /** Formats a y gridline's value. Defaults to a locale integer. */
  formatY?: (value: number) => string;
  /** Formats an x tick from its tick number — `formatMatchTime`, for the Match screen. Absent
   * ⇒ no x labels, because a raw tick number is not a reading. */
  formatX?: (tick: number) => string;
  /** Where x labels sit, as fractions of the domain. */
  xAt?: readonly number[];
  /** Gutters. Left holds the y labels; bottom holds the x labels and the season strip. */
  padLeft?: number;
  padBottom?: number;
  /** Right gutter for `endLabel`s. */
  padRight?: number;
  /**
   * Headroom above the plot. Without it the top gridline's label and any end label whose series
   * touches the maximum sit with their baseline at the viewBox edge, so the glyphs' ascenders are
   * clipped — "warrior 0" and the top axis number both rendered with their caps cut off on the
   * projector. A text baseline needs its font's ascent above it, so a chart with axis furniture
   * needs the plot's top pulled down by at least that much.
   */
  padTop?: number;
}

/**
 * Lays out labels along one axis so none overlap: each keeps its desired position where it can,
 * gets pushed to `gap` from its neighbour where it cannot, and the whole run stays inside
 * `[min, max]`.
 *
 * Exists because a composition's bands converge: early in a match every type has nearly the same
 * count, so the bands are thin and their end labels all want the same few pixels — "warrior 0"
 * printed over "builder 1". Pure and exported for direct testing; returns positions in the same
 * order as `desired`.
 */
export function layoutStackedLabels(
  desired: readonly number[],
  min: number,
  max: number,
  gap: number,
): number[] {
  const order = desired
    .map((y, index) => ({ y: Math.max(min, Math.min(max, y)), index }))
    .sort((a, b) => a.y - b.y || a.index - b.index);
  const placed: number[] = [];
  // Forward: nobody sits above their predecessor plus the gap.
  for (const [k, entry] of order.entries()) {
    placed.push(k === 0 ? entry.y : Math.max(entry.y, placed[k - 1] + gap));
  }
  // Backward: pull the run inside `max`, preserving the gap; re-clamp to `min` so an
  // over-full run degrades to touching labels rather than escaping the plot.
  for (let k = order.length - 1; k >= 0; k -= 1) {
    const upper = k === order.length - 1 ? max : placed[k + 1] - gap;
    placed[k] = Math.max(min, Math.min(placed[k], upper));
  }
  const out = new Array<number>(desired.length);
  order.forEach((entry, k) => {
    out[entry.index] = placed[k];
  });
  return out;
}

export interface TimeSeriesChartProps {
  series: readonly ChartSeries[];
  markers?: readonly ChartMarker[];
  /** The chart's x domain: ticks 0..totalTicks. */
  totalTicks: number;
  /** The y domain's top. Defaults to the largest value in `series` (never below 1). */
  maxValue?: number;
  /** 0-1 of the x domain that was never observed, shaded out. */
  unobservedFraction?: number;
  width?: number;
  height?: number;
  testId?: string;
  style?: CSSProperties;
  /** Accessible name; a chart with no label is invisible to a screen reader. */
  ariaLabel?: string;
  /** @see ChartAxes — absent leaves the pre-existing full-bleed geometry untouched. */
  axes?: ChartAxes;
  /**
   * `bands` (the default, and what Summary has always drawn) tints the whole plot per season.
   * `strip` demotes them to a bar under the x axis — the seasons are context, and context that
   * tints every pixel of the data is louder than the data.
   */
  seasons?: 'bands' | 'strip';
  /**
   * `rule` (default) draws a full-height dashed line per marker. `axisDot` puts a filled dot on
   * the baseline instead, so a prompt is legible as *when* without a coloured rule cutting
   * through the very series it annotates.
   */
  markerStyle?: 'rule' | 'axisDot';
  /**
   * Surface-coloured separator drawn between stacked areas, in px (the mark spec's 2px spacer).
   * Adjacent fills of similar lightness need it to stay separable; `0` (default) is the
   * pre-existing seamless stack.
   */
  bandGapPx?: number;
}

const DEFAULT_WIDTH = 340;
const DEFAULT_HEIGHT = 150;

/** Default x-label positions: both ends and the quarters. */
const DEFAULT_X_AT = [0, 0.25, 0.5, 0.75, 1] as const;

export function TimeSeriesChart({
  series,
  markers = [],
  totalTicks,
  maxValue,
  unobservedFraction = 0,
  width = DEFAULT_WIDTH,
  height = DEFAULT_HEIGHT,
  testId,
  style,
  ariaLabel,
  axes,
  seasons = 'bands',
  markerStyle = 'rule',
  bandGapPx = 0,
}: TimeSeriesChartProps) {
  const spanTicks = Math.max(1, totalTicks);
  const top = Math.max(
    1,
    maxValue ??
      series.reduce(
        (max, s) => Math.max(max, ...s.points.map((p) => p.value), ...(s.baseline ?? [0])),
        0,
      ),
  );

  // **The plot box.** With no `axes` every gutter is zero, so the box IS the viewBox and every
  // coordinate below is what it was before this prop existed — the property Summary's goldens rest
  // on. Gutters only open where a caller asked for the furniture that needs them.
  const padLeft = axes?.padLeft ?? 0;
  const padRight = axes?.padRight ?? 0;
  const padBottom = axes?.padBottom ?? 0;
  const padTop = axes?.padTop ?? 0;
  const plotWidth = Math.max(1, width - padLeft - padRight);
  const plotBottom = height - padBottom;
  const plotSpan = Math.max(1, plotBottom - padTop);

  const toX = (tick: number): number => padLeft + (tick / spanTicks) * plotWidth;
  const toY = (value: number): number => plotBottom - (value / top) * plotSpan;

  // End-label rows, collision-resolved once for the whole chart: a label's desired row is its
  // series' last value (a band's, the middle of the band), and `layoutStackedLabels` spreads the
  // collisions apart. 10px suits the 8.5px type; the clamp keeps ascenders inside the viewBox.
  const labelled = series.filter((s) => s.endLabel !== undefined && s.points.length > 0);
  const desiredLabelYs = labelled.map((s) => {
    const last = s.points[s.points.length - 1];
    const baseline = s.baseline?.[s.points.length - 1];
    return baseline === undefined ? toY(last.value) : (toY(last.value) + toY(baseline)) / 2;
  });
  const labelYs = layoutStackedLabels(desiredLabelYs, padTop + 4, plotBottom - 4, 10);
  const labelYById = new Map(labelled.map((s, i) => [s.id, labelYs[i]]));

  const formatY = axes?.formatY ?? ((value: number): string => Math.round(value).toLocaleString());
  const formatX = axes?.formatX;
  const ySteps = axes?.ySteps ?? 0;
  const xAt = axes?.xAt ?? DEFAULT_X_AT;

  return (
    <svg
      data-testid={testId}
      role={ariaLabel === undefined ? undefined : 'img'}
      aria-label={ariaLabel}
      viewBox={`0 0 ${width} ${height}`}
      style={{ width: '100%', display: 'block', ...style }}
    >
      {seasons === 'bands' &&
        CHART_SEASON_BANDS.map((band) => (
          <rect
            key={band.key}
            data-testid={`season-band-${band.key}`}
            x={padLeft + band.from * plotWidth}
            y={padTop}
            width={(band.to - band.from) * plotWidth}
            height={plotBottom - padTop}
            fill="var(--hud-track)"
            opacity={0.16}
          />
        ))}
      {unobservedFraction > 0 && (
        <rect
          data-testid="timeline-unobserved-range"
          x={padLeft}
          y={padTop}
          width={unobservedFraction * plotWidth}
          height={plotBottom - padTop}
          fill="var(--line-strong)"
          opacity={0.18}
        />
      )}
      {/* Gridlines first, so every mark sits over them — a grid that crosses the data reads as
          part of it. Recessive by token, never a full-strength line. */}
      {ySteps > 0 &&
        Array.from({ length: ySteps }, (_, i) => i + 1).map((step) => {
          const value = (top * step) / ySteps;
          const y = toY(value);
          return (
            <g key={`grid-${step}`}>
              <line
                data-testid={`chart-gridline-${step}`}
                x1={padLeft}
                x2={padLeft + plotWidth}
                y1={y}
                y2={y}
                stroke="var(--hud-track)"
                strokeWidth={1}
              />
              <text
                data-testid={`chart-y-label-${step}`}
                x={padLeft - 4}
                y={y + 3}
                textAnchor="end"
                fontSize={8.5}
                fill="var(--hud-text-dim)"
              >
                {formatY(value)}
              </text>
            </g>
          );
        })}
      <line x1={padLeft} y1={plotBottom} x2={padLeft + plotWidth} y2={plotBottom} stroke="var(--line)" />
      {formatX !== undefined &&
        xAt.map((fraction) => (
          <text
            key={`x-${fraction}`}
            data-testid={`chart-x-label-${fraction}`}
            x={padLeft + fraction * plotWidth}
            y={plotBottom + 20}
            textAnchor={fraction === 0 ? 'start' : fraction === 1 ? 'end' : 'middle'}
            fontSize={8.5}
            fill="var(--hud-text-dim)"
          >
            {formatX(Math.round(fraction * spanTicks))}
          </text>
        ))}
      {seasons === 'strip' &&
        CHART_SEASON_BANDS.map((band) => (
          <rect
            key={band.key}
            data-testid={`season-band-${band.key}`}
            x={padLeft + band.from * plotWidth}
            y={plotBottom + 6}
            width={(band.to - band.from) * plotWidth}
            height={4}
            rx={2}
            fill={`var(--season-${band.key})`}
            opacity={0.85}
          />
        ))}
      {series.map((s) => {
        const testIdForSeries = s.testId ?? `chart-series-${s.id}`;
        if (s.kind === 'area') {
          // Forward along the top edge, back along the baseline (or the floor).
          const forward = s.points.map((p) => `${toX(p.tick)},${toY(p.value)}`);
          const back = s.points
            .map((p, i) => `${toX(p.tick)},${toY(s.baseline?.[i] ?? 0)}`)
            .reverse();
          return (
            <polygon
              key={s.id}
              data-testid={testIdForSeries}
              fill={s.colour}
              // The gap is a surface-coloured stroke rather than inset geometry: it costs no
              // coordinate arithmetic and cannot round a band into a negative height.
              stroke={bandGapPx > 0 ? 'var(--hud-bg)' : 'none'}
              strokeWidth={bandGapPx > 0 ? bandGapPx : undefined}
              opacity={s.opacity ?? 0.85}
              points={[...forward, ...back].join(' ')}
            />
          );
        }
        return (
          <polyline
            key={s.id}
            data-testid={testIdForSeries}
            fill="none"
            stroke={s.colour}
            strokeWidth={s.strokeWidth ?? 2}
            strokeDasharray={s.dashed === true ? '4 3' : undefined}
            opacity={s.opacity ?? 1}
            points={s.points.map((p) => `${toX(p.tick)},${toY(p.value)}`).join(' ')}
          />
        );
      })}
      {/* Direct labels, drawn after the marks so nothing paints over them. Swatch in the series
          colour, text in ink (`marks-and-anatomy`: text wears text tokens). The row is the
          collision-resolved one computed above, not the raw geometric position — converging bands
          would otherwise print their labels over each other. */}
      {labelled.map((s) => {
        const y = labelYById.get(s.id) as number;
        return (
          <g key={`label-${s.id}`} data-testid={`chart-end-label-${s.id}`}>
            <rect x={padLeft + plotWidth + 5} y={y - 4.5} width={9} height={9} rx={2} fill={s.colour} />
            <text x={padLeft + plotWidth + 18} y={y + 3} fontSize={8.5} fontWeight={600} fill="var(--hud-text)">
              {s.endLabel}
            </text>
          </g>
        );
      })}
      {markers.map((marker) =>
        markerStyle === 'axisDot' ? (
          <g key={marker.id} data-testid={`chart-marker-${marker.id}`}>
            <circle
              cx={toX(marker.tick)}
              cy={plotBottom}
              r={5.5}
              fill={marker.colour}
              stroke="var(--hud-bg)"
              strokeWidth={2}
            />
            {marker.label !== undefined && (
              <text
                x={toX(marker.tick)}
                y={plotBottom + 2.8}
                textAnchor="middle"
                fontSize={7}
                fontWeight={800}
                fill="var(--hud-bg)"
              >
                {marker.label}
              </text>
            )}
            {marker.title !== undefined && <title>{marker.title}</title>}
          </g>
        ) : (
          <line
            key={marker.id}
            data-testid={`chart-marker-${marker.id}`}
            x1={toX(marker.tick)}
            x2={toX(marker.tick)}
            y1={padTop}
            y2={plotBottom}
            stroke={marker.colour}
            strokeWidth={1.5}
            strokeDasharray="3 2"
            opacity={0.9}
          >
            {marker.title !== undefined && <title>{marker.title}</title>}
          </line>
        ),
      )}
    </svg>
  );
}
